/**
 * LiveGameConfigDialog — config form for a new LIVE game.
 *
 * Replaces the openConfigGameDialog from EstadisticasNew.js. Same fields,
 * v2 styling.
 */

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, TextField, MenuItem, Button, Typography,
} from '@mui/material';
import { useStatsTheme } from '../../theme/statsTheme';

const DEFAULT_GAME = () => ({
  game_label: '',
  adversary_name: '',
  number_of_periods: 4,
  period_duration: 10,
  game_date: new Date().toISOString().split('T')[0],
  game_location: 'domicile',
  notes: '',
});

export default function LiveGameConfigDialog({ open, onClose, onConfirm, t }) {
  const theme = useStatsTheme();
  const [game, setGame] = useState(DEFAULT_GAME);
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  const update = (k, v) => setGame((prev) => ({ ...prev, [k]: v }));

  const handleConfirm = () => {
    if (!game.game_label.trim()) return;
    onConfirm(game);
    setGame(DEFAULT_GAME());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.surfaces.elevated,
          color: theme.text.primary,
          border: `1px solid ${theme.accentBorder}`,
          borderRadius: theme.radius.xl,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{tt('configureGame', 'Configure live game')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 1.5, mt: 1 }}>
          <TextField
            label={tt('gameLabel', 'Game label')}
            value={game.game_label}
            onChange={(e) => update('game_label', e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label={tt('adversary', 'Adversary')}
            value={game.adversary_name}
            onChange={(e) => update('adversary_name', e.target.value)}
            fullWidth
            size="small"
          />
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
            <TextField
              label={tt('periods', 'Periods')}
              type="number"
              value={game.number_of_periods}
              onChange={(e) => update('number_of_periods', Number(e.target.value) || 4)}
              size="small"
            />
            <TextField
              label={tt('periodDuration', 'Period duration (min)')}
              type="number"
              value={game.period_duration}
              onChange={(e) => update('period_duration', Number(e.target.value) || 10)}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
            <TextField
              label={tt('date', 'Date')}
              type="date"
              value={game.game_date}
              onChange={(e) => update('game_date', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label={tt('location', 'Location')}
              value={game.game_location}
              onChange={(e) => update('game_location', e.target.value)}
              size="small"
            >
              <MenuItem value="domicile">{tt('home', 'Home')}</MenuItem>
              <MenuItem value="exterieur">{tt('away', 'Away')}</MenuItem>
            </TextField>
          </Box>
          <TextField
            label={tt('notes', 'Notes')}
            value={game.notes}
            onChange={(e) => update('notes', e.target.value)}
            multiline
            rows={2}
            size="small"
            fullWidth
          />
        </Box>
        {!game.game_label.trim() && (
          <Typography sx={{ fontSize: 11, color: theme.actionColors.warning, mt: 1 }}>
            {tt('gameLabelRequired', 'A game label is required')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: theme.text.secondary }}>
          {tt('cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!game.game_label.trim()}
          sx={{
            backgroundColor: theme.accent,
            '&:hover': { backgroundColor: theme.accentStrong },
          }}
        >
          {tt('start', 'Start')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
